import React, { useCallback } from "react";
import { connect } from "react-redux";

import AccessStatus from "@mapmycustomers/shared/types/entity/common/AccessStatus";
import RawFile from "@mapmycustomers/shared/types/File";

import ExtendedAnalytics from "@app/component/analytics/ExtendedAnalytics";
import FileContent from "@app/component/FileContent/FilesContent";
import FileUploaderModal from "@app/component/FileUploaderModal";
import {
  clearAllUploadedTripFiles,
  deleteTripFile,
  downloadTripFile,
  fetchThumbnail,
  uploadTripFiles,
} from "@app/scene/reports/store/actions";
import { areFilesUploading } from "@app/scene/reports/store/selectors";
import { RootState } from "@app/store/rootReducer";
import FileListItem from "@app/types/FileListItem";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import useAnalytics from "@app/util/contexts/useAnalytics";
import useModalVisibilityAnalyticEffect from "@app/util/hook/useModalVisibilityAnalyticEffect";

interface FilesProps {
  accessStatus?: AccessStatus;
  addingFile?: boolean;
  files: RawFile[];
  onStopAddingFile: () => void;
}

interface Props extends FilesProps {
  clearAllUploadedFiles: () => void;
  fileUploading?: boolean;
  onDelete?: (fileId: RawFile) => void;
  onDownload?: (file: RawFile) => void;
  onFetchThumbnail?: (payload: { callback: (blob: Blob) => void; fileId: RawFile["id"] }) => void;
  onUpload?: (payload: {
    callback?: (filesList: FileListItem[]) => void;
    files: (Blob | File)[];
  }) => void;
}

const Files: React.FC<Props> = ({
  accessStatus,
  addingFile,
  clearAllUploadedFiles,
  files,
  fileUploading,
  onDelete,
  onDownload,
  onFetchThumbnail,
  onStopAddingFile,
  onUpload,
}) => {
  const filesAnalytics = useExtendedAnalytics("Files", useAnalytics());
  useModalVisibilityAnalyticEffect("Add File", !!addingFile, filesAnalytics);

  const handleDownload = useCallback(
    (file: RawFile) => {
      filesAnalytics?.clicked(["Download File"]);
      onDownload?.(file);
    },
    [filesAnalytics, onDownload]
  );

  const handleDelete = useCallback(
    (file: RawFile) => {
      filesAnalytics?.clicked(["Delete File"]);
      onDelete?.(file);
    },
    [filesAnalytics, onDelete]
  );

  return (
    <div>
      {files.length > 0 && (
        <div>
          <FileContent
            accessStatus={accessStatus}
            files={files}
            onDelete={handleDelete}
            onDownload={handleDownload}
            onFetchThumbnail={onFetchThumbnail}
          />
        </div>
      )}

      {addingFile && (
        <ExtendedAnalytics keyPath="Files">
          <FileUploaderModal
            clearAllUploadedFiles={clearAllUploadedFiles}
            fileUploading={fileUploading}
            onDelete={onDelete}
            onHide={onStopAddingFile}
            onUpload={onUpload}
          />
        </ExtendedAnalytics>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  fileUploading: areFilesUploading(state),
});

const mapDispatchToProps = {
  clearAllUploadedFiles: clearAllUploadedTripFiles,
  onDelete: deleteTripFile.request,
  onDownload: downloadTripFile,
  onFetchThumbnail: fetchThumbnail,
  onUpload: uploadTripFiles.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(Files);
